/**
 * 포스터 글꼴(D6 · Canva 실측 2026-10-01 jery) — **Canva 가 이름으로 알아본 것만** 쓴다.
 * ❌ Gothic A1 은 넣지 않는다(Canva 가 Copperplate 로 바꾼다).
 *
 * 🔑 편집 화면은 구글 웹폰트로 그리고, PDF 는 같은 글꼴의 TTF(`/fonts/poster/*`)를 잘라 넣는다(PR3).
 * 🔑 줄바꿈은 편집 화면에서 한 번 계산해 PDF·PNG 가 같은 줄을 쓴다(layout.ts · D4).
 */
export interface PosterFont {
  key: string;
  label: string;
  /** CSS font-family 이름(구글 웹폰트) */
  family: string;
  /** 용도 표시 */
  role: "제목" | "본문";
  /** 굵게를 쓸 수 있는가 — 굵은 글씨가 없는 글꼴은 Canva 도 B 를 끈다 */
  bold: boolean;
  /** 보통 굵기를 그릴 때 쓰는 실제 CSS 굵기(Sunflower 는 Medium 하나뿐) */
  regularWeight: number;
  /** PDF 에 넣을 글꼴 파일(WOFF 1판 · `/fonts/poster/`). 🚨 WOFF2 는 잘라 넣기에서 깨진다(실측) */
  files: { regular: string; bold?: string };
  /**
   * 이 글꼴에 글자 모양이 없는 것 — 쓰면 편집 화면에서 알린다(인쇄하면 빈칸이 된다).
   * 2026-10-01 실측: fontTools cmap 으로 자주 쓰는 기호 60여 개를 대조했다(한글·영문·숫자는 전부 있다).
   */
  missing: string;
}

export const POSTER_FONTS: readonly PosterFont[] = [
  { key: "black-han-sans", label: "검은고딕", family: "Black Han Sans", role: "제목", bold: false, regularWeight: 400, files: { regular: "BlackHanSans-Regular.woff" }, missing: "·–—…※★☆♥♡♪♬→←↑↓○●◎□■△▲▽▼◇◆♣♠✓✔①②③㈜℃「」『』《》〈〉" },
  { key: "jua", label: "주아", family: "Jua", role: "제목", bold: false, regularWeight: 400, files: { regular: "Jua-Regular.woff" }, missing: "·–—…‘’※♪♬→←↑↓○●◎□■△▲▽▼◇◆♣♠✓✔①②③㈜℃「」『』《》〈〉×÷" },
  { key: "do-hyeon", label: "도현", family: "Do Hyeon", role: "제목", bold: false, regularWeight: 400, files: { regular: "DoHyeon-Regular.woff" }, missing: "·–—…※✓✔「」『』《》〈〉" },
  { key: "sunflower", label: "해바라기", family: "Sunflower", role: "제목", bold: false, regularWeight: 500, files: { regular: "Sunflower-Medium.woff" }, missing: "·–—…※★☆♥♡♪♬→←↑↓○●◎□■△▲▽▼◇◆♣♠✓✔①②③㈜℃「」『』《》〈〉" },
  { key: "noto-sans-kr", label: "본고딕", family: "Noto Sans KR", role: "본문", bold: true, regularWeight: 400, files: { regular: "NotoSansKR-Regular.woff", bold: "NotoSansKR-Bold.woff" }, missing: "✔" },
  { key: "nanum-gothic", label: "나눔고딕", family: "Nanum Gothic", role: "본문", bold: true, regularWeight: 400, files: { regular: "NanumGothic-Regular.woff", bold: "NanumGothic-Bold.woff" }, missing: "✓✔①②③㈜℃" },
  { key: "gowun-dodum", label: "고운돋움", family: "Gowun Dodum", role: "본문", bold: false, regularWeight: 400, files: { regular: "GowunDodum-Regular.woff" }, missing: "✓✔" },
  { key: "nanum-myeongjo", label: "나눔명조", family: "Nanum Myeongjo", role: "본문", bold: false, regularWeight: 400, files: { regular: "NanumMyeongjo-Regular.woff" }, missing: "✓✔①②③℃" },
  { key: "ibm-plex-sans-kr", label: "IBM 플렉스", family: "IBM Plex Sans KR", role: "본문", bold: false, regularWeight: 400, files: { regular: "IBMPlexSansKR-Regular.woff" }, missing: "✓✔" },
];

export const DEFAULT_TITLE_FONT = "black-han-sans";
export const DEFAULT_BODY_FONT = "noto-sans-kr";

export function posterFont(key: string): PosterFont {
  return POSTER_FONTS.find((font) => font.key === key) ?? POSTER_FONTS.find((font) => font.key === DEFAULT_BODY_FONT)!;
}

/** 실제로 그릴 CSS 굵기 — 굵게가 없는 글꼴에서 700 을 요구하면 브라우저가 가짜 굵게를 만든다(PDF 와 달라진다). */
export function cssWeight(font: PosterFont, weight: 400 | 700): number {
  return weight === 700 && font.bold ? 700 : font.regularWeight;
}

/** 고른 글꼴에 없는 글자 — 편집 화면 경고용(구현 시 확인 목록) */
export function missingGlyphs(fontKey: string, text: string): string[] {
  const font = posterFont(fontKey);
  return [...new Set([...text].filter((char) => font.missing.includes(char)))];
}

const GOOGLE_FONTS_URL = "https://fonts.googleapis.com/css2?"
  + [
    "family=Black+Han+Sans",
    "family=Jua",
    "family=Do+Hyeon",
    "family=Sunflower:wght@500",
    "family=Noto+Sans+KR:wght@400;700",
    "family=Nanum+Gothic:wght@400;700",
    "family=Gowun+Dodum",
    "family=Nanum+Myeongjo",
    "family=IBM+Plex+Sans+KR",
  ].join("&")
  + "&display=swap";

let loading: Promise<void> | null = null;

/**
 * 포스터 화면에서만 글꼴을 불러온다(다른 수업 화면은 영향 없음).
 * 🔑 줄바꿈 계산 전에 반드시 기다린다 — 글꼴이 오기 전에 재면 대체 글꼴 폭으로 줄이 끊긴다.
 */
export function loadPosterFonts(): Promise<void> {
  if (loading) return loading;
  loading = (async () => {
    if (typeof document === "undefined") return;
    if (!document.querySelector("link[data-poster-fonts]")) {
      const link = document.createElement("link");
      link.rel = "stylesheet";
      link.href = GOOGLE_FONTS_URL;
      link.dataset.posterFonts = "1";
      document.head.appendChild(link);
      await new Promise<void>((resolve) => {
        link.addEventListener("load", () => resolve(), { once: true });
        link.addEventListener("error", () => resolve(), { once: true });
      });
    }
    // 한글 범위의 글자 하나로 실제 파일을 당겨 온다(unicode-range 조각).
    await Promise.all(POSTER_FONTS.flatMap((font) => {
      const weights = font.bold ? [font.regularWeight, 700] : [font.regularWeight];
      return weights.map((weight) =>
        document.fonts.load(`${weight} 16px "${font.family}"`, "가나다ABC123·").catch(() => [])
      );
    }));
  })();
  return loading;
}
